import React from 'react';
import { 
  Box, 
  Typography, 
  Tooltip,
  CircularProgress
} from '@mui/material';
import { 
  Timer as TimerIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

interface TimeSavedIndicatorProps {
  minutes: number;
  showIcon?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'compact' | 'circular';
}

const TimeSavedIndicator: React.FC<TimeSavedIndicatorProps> = ({
  minutes,
  showIcon = true,
  size = 'medium',
  variant = 'default'
}) => {
  // Format time saved
  const formatTimeSaved = (): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (remainingMinutes === 0) {
        return `${hours} hr`;
      } else {
        return `${hours} hr ${remainingMinutes} min`;
      }
    }
  };
  
  // Get font size based on size prop
  const getFontSize = (): string => {
    switch (size) {
      case 'small':
        return '0.75rem';
      case 'large':
        return '1.25rem';
      case 'medium':
      default:
        return '0.875rem';
    }
  };
  
  // Get icon size based on size prop
  const getIconSize = (): 'small' | 'medium' | 'large' => {
    switch (size) {
      case 'small':
        return 'small';
      case 'large':
        return 'large';
      case 'medium':
      default:
        return 'medium';
    }
  };
  
  // Circular variant
  if (variant === 'circular') {
    const circleSize = size === 'small' ? 40 : size === 'large' ? 80 : 60;
    
    return (
      <Tooltip title={`You saved ${formatTimeSaved()} by using SharkPanzer`}>
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <CircularProgress
            variant="determinate"
            value={100}
            size={circleSize}
            thickness={4}
            sx={{ color: 'success.main' }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            <SpeedIcon color="success" fontSize={getIconSize()} />
            <Typography
              variant="caption"
              component="div"
              color="success.main"
              sx={{ 
                fontWeight: 'bold',
                fontSize: getFontSize(),
                lineHeight: 1
              }}
            >
              {formatTimeSaved()}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  }
  
  // Compact variant
  if (variant === 'compact') {
    return (
      <Tooltip title={`You saved ${formatTimeSaved()} by using SharkPanzer`}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            bgcolor: 'success.main',
            color: 'success.contrastText',
            borderRadius: 10,
            px: 1,
            py: 0.5
          }}
        >
          {showIcon && <TimerIcon fontSize="inherit" sx={{ mr: 0.5 }} />}
          <Typography
            variant="caption"
            sx={{ 
              fontWeight: 'bold',
              fontSize: getFontSize(),
              lineHeight: 1
            }}
          >
            {formatTimeSaved()}
          </Typography>
        </Box>
      </Tooltip>
    );
  }
  
  // Default variant
  return (
    <Tooltip title="Time saved by using SharkPanzer instead of watching the full video">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          bgcolor: 'rgba(6, 214, 160, 0.1)',
          color: '#06d6a0',  // Brighter green color
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          border: '1px solid rgba(6, 214, 160, 0.3)',  // Add border for prominence
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'   // Subtle shadow
        }}
      >
        {showIcon && <TimerIcon fontSize={getIconSize()} sx={{ mr: 0.5 }} />}
        <Box>
          <Typography
            variant="caption"
            sx={{ 
              display: 'block',
              fontSize: '0.7rem',
              lineHeight: 1
            }}
          >
            TIME SAVED
          </Typography>
          <Typography
            variant="body2"
            sx={{ 
              fontWeight: 'bold',
              fontSize: getFontSize(),
              lineHeight: 1.2
            }}
          >
            {formatTimeSaved()}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TimeSavedIndicator;
